"use client";

import "react-day-picker/style.css";

import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Box, InputAdornment, Popover, TextField } from "@mui/material";
import { alpha } from "@mui/material/styles";
import dayjs, { type Dayjs } from "dayjs";
import { type MouseEvent, useState } from "react";
import { DayPicker } from "react-day-picker";

interface CustomDatePickerProps {
    label?: string;
    value: Dayjs | null;
    onChange: (date: Dayjs | null) => void;
    disablePast?: boolean;
    minDate?: Dayjs;
    maxDate?: Dayjs;
    fullWidth?: boolean;
    size?: "small" | "medium";
}

export const CustomDatePicker = ({
    label,
    value,
    onChange,
    disablePast = false,
    minDate,
    maxDate,
    fullWidth = true,
    size = "medium",
}: CustomDatePickerProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleOpenPopover = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <TextField
                fullWidth={fullWidth}
                label={label || undefined}
                hiddenLabel={!label}
                size={size}
                value={value ? value.format("DD/MM/YYYY") : ""}
                onClick={handleOpenPopover}
                slotProps={{
                    input: {
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <CalendarTodayIcon sx={{ fontSize: 20, color: "action.active" }} />
                            </InputAdornment>
                        ),
                    },
                }}
                sx={{
                    "& .MuiInputBase-root": {
                        cursor: "pointer",
                    },
                    "& .MuiOutlinedInput-input": {
                        cursor: "pointer",
                    },
                }}
            />
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                            borderRadius: 2,
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        p: 1.5,
                        "& .rdp-root": {
                            "--rdp-accent-color": "#158f1b",
                            "--rdp-accent-background-color": alpha("#158f1b", 0.1),
                            "--rdp-day-hover-background-color": alpha("#158f1b", 0.05),
                            "--rdp-range_middle-background-color": alpha("#158f1b", 0.1),
                            "--rdp-range_start-color": "#158f1b",
                            "--rdp-range_end-color": "#158f1b",
                            fontFamily: "inherit",
                        },
                        "& .rdp-selected .rdp-day_button": {
                            borderColor: "#158f1b !important",
                            backgroundColor: "transparent !important",
                            fontWeight: "700",
                        },
                        "& .rdp-today": {
                            color: "#158f1b !important",
                            fontWeight: "800",
                            textDecoration: "underline",
                        },
                        "& .rdp-chevron": {
                            fill: "#158f1b !important",
                        },
                    }}
                >
                    <DayPicker
                        mode="single"
                        selected={value ? value.toDate() : undefined}
                        // Default is the browser's current month, which differs from the selected
                        // (store-local) date's month around a month boundary.
                        defaultMonth={value ? value.toDate() : undefined}
                        onSelect={(date) => {
                            onChange(date ? dayjs(date) : null);
                            if (date) {
                                handleClosePopover();
                            }
                        }}
                        disabled={(date) => {
                            if (minDate && date < minDate.startOf("day").toDate()) {
                                return true;
                            }
                            if (disablePast && date < dayjs().startOf("day").toDate()) {
                                return true;
                            }
                            if (maxDate && date > maxDate.endOf("day").toDate()) {
                                return true;
                            }
                            return false;
                        }}
                    />
                </Box>
            </Popover>
        </>
    );
};
